import { useCallback, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useAuthContext } from "@covertree/auth";
import { Toast } from "@covertree/utils";

import Drawer from "@mui/material/Drawer";

import CognitoUserGroup from "@enums/CognitoUserGroup";

import { useIssueTLLForResidentMutation } from "@redux/coverages/coveragesApi";
import {
  useGetResidentQuery,
  useGetReviewDetailsQuery,
} from "@redux/residents/residentsApi";

import ReviewDetailsContent from "./ReviewDetailsContent";

export default function ReviewDetails() {
  const { residentId: id } = useParams();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const [isIssueTllModalOpened, setIsIssueTllModalOpened] = useState(false);

  const { hasGroup } = useAuthContext();

  const {
    data: resident,
    isFetching: isFetchingResident,
    isError: isResidentError,
  } = useGetResidentQuery(id ? { id } : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const {
    currentData: reviewDetails,
    isFetching,
    isError: isReviewError,
  } = useGetReviewDetailsQuery(id ? { id } : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const [issueTllForResident, { isLoading: isIssueTllLoading }] =
    useIssueTLLForResidentMutation();

  const handleIssueTll = useCallback(async () => {
    try {
      await issueTllForResident({ id }).unwrap();
      navigate({
        pathname: `../resident/${encodeURIComponent(id)}`,
        search: createSearchParams(searchParams).toString(),
      });
    } catch {
      setIsIssueTllModalOpened(false);
      Toast.showError("Something went wrong while issuing BRL");
    }
  }, [navigate, issueTllForResident, searchParams, setIsIssueTllModalOpened]);

  const onClose = useCallback(() => {
    navigate({
      pathname: "../",
      search: createSearchParams(searchParams).toString(),
    });
  }, [searchParams]);

  return (
    <Drawer
      anchor="right"
      open={!!id}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: {
            xs: "45%",
            xl: "35%",
          },
        },
      }}>
      <ReviewDetailsContent
        canIssueTll={hasGroup([
          CognitoUserGroup.CoverTreeExecutive,
          CognitoUserGroup.CoverTreeEmployee,
        ])}
        changeIssueTllModalOpened={setIsIssueTllModalOpened}
        isError={isReviewError || isResidentError}
        isLoadingResidentDetails={isFetchingResident || !id}
        isLoadingReviewDetails={isFetching || !id}
        isIssueTllLoading={isIssueTllLoading}
        isIssueTllModalOpened={isIssueTllModalOpened}
        issueTll={handleIssueTll}
        resident={id && resident?.id === id ? resident : undefined}
        reviewDetails={id && reviewDetails ? reviewDetails : undefined}
        onClose={onClose}
      />
    </Drawer>
  );
}
